import React, {useState, useEffect, useMemo} from "react";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import {
    Typography, Row, Col, Card, Table, InputNumber, Button, Modal, Form, DatePicker, message, Radio, Select, Space
} from "antd";
import {
    LineChart, BarChart, XAxis, YAxis, Tooltip, Legend, Line, Bar, CartesianGrid, ResponsiveContainer, PieChart, Pie,
    Cell
} from "recharts";
import api from "../../api";
import dayjs from "dayjs";

const {Title} = Typography;
const {Option} = Select;

type AnalyticsEntry = {
    id: number;
    month: string; // "YYYY-MM-DD"
    customer_count: number;
    theo: number;
};

type AgeBucket = {
    name: string;
    value: number;
};

type ChartItem = {
    name: string;
    value: number;
};

const prefectureToMapName: Record<string, string> = {
    Aichi: "Aichi",
    Akita: "Akita",
    Aomori: "Aomori",
    Chiba: "Chiba",
    Ehime: "Ehime",
    Fukui: "Fukui",
    Fukuoka: "Fukuoka",
    Fukushima: "Fukushima",
    Gifu: "Gifu",
    Gunma: "Gunma",
    Hiroshima: "Hiroshima",
    Hokkaido: "Hokkaidō",
    Hyogo: "Hyōgo",
    Ibaraki: "Ibaraki",
    Ishikawa: "Ishikawa",
    Iwate: "Iwate",
    Kagawa: "Kagawa",
    Kagoshima: "Kagoshima",
    Kanagawa: "Kanagawa",
    Kochi: "Kōchi",
    Kumamoto: "Kumamoto",
    Kyoto: "Kyōto",
    Mie: "Mie",
    Miyagi: "Miyagi",
    Miyazaki: "Miyazaki",
    Nagano: "Nagano",
    Nagasaki: "Nagasaki",
    Nara: "Nara",
    Niigata: "Niigata",
    Oita: "Ōita",
    Okayama: "Okayama",
    Okinawa: "Okinawa",
    Osaka: "Ōsaka",
    Saga: "Saga",
    Saitama: "Saitama",
    Shiga: "Shiga",
    Shimane: "Shimane",
    Shizuoka: "Shizuoka",
    Tochigi: "Tochigi",
    Tokushima: "Tokushima",
    Tokyo: "Tokyo",
    Tottori: "Tottori",
    Toyama: "Toyama",
    Wakayama: "Wakayama",
    Yamagata: "Yamagata",
    Yamaguchi: "Yamaguchi",
    Yamanashi: "Yamanashi",
};

const CCAnalyticsPage: React.FC = () => {
    const [data, setData] = useState<AnalyticsEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<any>(null);
    const [showBarChart, setShowBarChart] = useState(true);
    const [showCustomerBarChart, setShowCustomerBarChart] = useState(true);
    const [reservationData, setReservationData] = useState<any[]>([]);
    const [uniqueReservationData, setUniqueReservationData] = useState<any[]>([]);
    const [nationalityData, setNationalityData] = useState<any[]>([]);
    const [ageBucketData, setAgeBucketData] = useState<any[]>([]);
    const [registrationCounts, setRegistrationCounts] = useState<any>({
        daily: [],
        monthly: [],
        quarterly: [],
    });
    const [registrationGranularity, setRegistrationGranularity] = useState<"daily" | "monthly" | "quarterly">("monthly");
    const [form] = Form.useForm();

    const now = dayjs();
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [exportYear, setExportYear] = useState<number>(now.year());
    const [exportMonth, setExportMonth] = useState<number>(now.month() + 1);
    const [prefectureData, setPrefectureData] = useState<ChartItem[]>([]);
    const [cityData, setCityData] = useState<ChartItem[]>([]);
    const [mapReady, setMapReady] = useState(false);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });

    const chartData = useMemo(() => {
        return [...data].sort((a, b) => a.month.localeCompare(b.month));
    }, [data]);

    const fetchData = async (page: number, pageSize: number) => {
        try {
            setLoading(true);

            const res = await api.get("/cc/analytics/", {
                params: {page, page_size: pageSize},
            });

            const results = res.data.results ?? [];

            setTotal(res.data.count ?? results.length);
            setData(
                results.map((d: AnalyticsEntry) => ({
                    ...d,
                    theo: typeof d.theo === "string" ? parseFloat(d.theo) : d.theo,
                }))
            );
        } catch {
            message.error("Failed to fetch data.");
        } finally {
            setLoading(false);
        }
    };

    const fetchReservationData = async () => {
        try {
            const res = await api.get("/cc/reservation-analytics/");
            const sorted = (res.data.results || res.data).sort(
                (a: any, b: any) => a.month.localeCompare(b.month)
            );
            setReservationData(sorted);
        } catch (err) {
            message.error("Failed to fetch reservations.");
        }
    };

    const fetchUniqueReservationData = async () => {
        try {
            const res = await api.get("/cc/unique-reservation-analytics/");
            const sorted = (res.data.results || res.data).sort(
                (a: any, b: any) => a.month.localeCompare(b.month)
            );
            setUniqueReservationData(sorted);
        } catch (err) {
            message.error("Failed to fetch unique reservations.");
        }
    };

    const fetchRegistrationCounts = async () => {
        try {
            const res = await api.get("/cc/registration-counts/");
            setRegistrationCounts(res.data);
        } catch (err) {
            message.error("Failed to fetch registration counts.");
        }
    };

    const fetchNationalityData = async () => {
        try {
            const res = await api.get("/cc/customer-nationality/?top=12");
            setNationalityData(
                (res.data || []).map((d: any) => ({name: d.name, value: Number(d.value) || 0}))
            );
        } catch {
            message.error("Failed to fetch nationality breakdown.");
        }
    };

    const fetchPrefectureData = async () => {
        try {
            const res = await api.get("/cc/customer-by-prefecture/");

            const formatted = (res.data.results || res.data).map((item: any) => ({
                name: item.prefecture,
                value: item.count,
            }));

            // sort descending
            formatted.sort((a: any, b: any) => b.value - a.value);

            setPrefectureData(formatted);
        } catch (err) {
            message.error("Failed to fetch prefecture data.");
        }
    };

    const fetchCityData = async (prefecture?: string) => {
        try {
            const res = await api.get("/cc/customer-by-city/", {
                params: prefecture ? {prefecture} : {},
            });

            const formatted = (res.data.results || res.data).map((item: any) => ({
                name: item.city,
                value: item.count,
            }));

            formatted.sort((a: any, b: any) => b.value - a.value);

            setCityData(formatted);
        } catch (err) {
            message.error("Failed to fetch city data.");
        }
    };

    const fetchJapanMap = async () => {
        try {
            const res = await fetch("/jp.json");
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }

            const geoJson = await res.json();
            console.log("jp.json loaded:", geoJson);

            echarts.registerMap("japan", geoJson);
            setMapReady(true);
        } catch (err) {
            console.error("Failed to load Japan map:", err);
            message.error("Failed to load Japan map.");
        }
    };

    useEffect(() => {
        fetchJapanMap();
    }, []);

    const fetchAgeBucketData = async () => {
        try {
            const res = await api.get("/cc/customer-age-buckets/");
            const raw: AgeBucket[] = (res.data || []).map((d: any): AgeBucket => ({
                name: d.name,
                value: Number(d.value) || 0,
            }));

            const filtered: AgeBucket[] = raw.filter((item: AgeBucket) => {
                if (!item.name) return false;
                const parts = item.name.split("-");
                const start = parseInt(parts[0], 10);
                return isNaN(start) || start >= 20;
            });

            setAgeBucketData(filtered);
        } catch {
            message.error("Failed to fetch age buckets.");
        }
    };

    const fetchCurrentUser = async () => {
        try {
            const res = await api.get("/users/me/");
            setCurrentUser(res.data);
        } catch (err) {
            setCurrentUser(null);
        }
    };

    useEffect(() => {
        fetchData(pagination.current, pagination.pageSize);
        fetchReservationData();
        fetchUniqueReservationData();
        fetchRegistrationCounts();
        fetchNationalityData();
        fetchAgeBucketData();
        fetchPrefectureData();
        fetchCityData();
        fetchJapanMap();
        fetchCurrentUser();
    }, [pagination.current, pagination.pageSize]);

    const handleSave = async (values: any) => {
        const payload = {
            ...values,
            month: values.month.format("YYYY-MM-DD"),
        };
        try {
            if (editing) {
                await api.put(`/cc/analytics/${editing.id}/`, payload);
                message.success("Data updated.");
            } else {
                await api.post("/cc/analytics/", payload);
                message.success("Data added.");
            }
            fetchData(pagination.current, pagination.pageSize);
            fetchReservationData();
            fetchUniqueReservationData();
            fetchRegistrationCounts();
            setModalOpen(false);
            form.resetFields();
            setEditing(null);
        } catch (err) {
            message.error("Failed to save data.");
        }
    };

    const openModal = (record?: any) => {
        if (record) {
            form.setFieldsValue({
                ...record,
                month: dayjs(record.month),
            });
            setEditing(record);
        } else {
            form.resetFields();
            setEditing(null);
        }
        setModalOpen(true);
    };

    const handleExportPdf = async () => {
        try {
            const res = await api.get("/cc/reports/monthly/pdf/", {
                params: {year: exportYear, month: exportMonth},
                responseType: "blob", // <-- so we get the PDF bytes
            });

            const blob = new Blob([res.data], {type: "application/pdf"});
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `cc-report-${exportYear}-${String(exportMonth).padStart(2, "0")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            message.error("Failed to export PDF.");
        }
    };

    const japanMapOption = {
        tooltip: {
            trigger: "item",
            formatter: (params: any) =>
                `${params.name}: ${params.value || 0} customers`,
        },
        visualMap: {
            min: 0,
            max: prefectureData.length ? Math.max(...prefectureData.map((x) => x.value)) : 1,
            left: "left",
            bottom: 20,
            text: ["High", "Low"],
            calculable: true,
        },
        series: [
            {
                name: "Customers",
                type: "map",
                map: "japan",
                roam: true,
                label: {show: false},
                data: prefectureData.map((item) => ({
                    name: prefectureToMapName[item.name] || item.name,
                    value: item.value,
                })),
            },
        ],
    };

    const renderCustomLabel = (props: any) => {
        const {x, y, width, height, value} = props;

        return (
            <text
                x={x + width + 5}
                y={y + height / 2}
                fill="#333"
                fontSize={12}
                dominantBaseline="middle"
            >
                {value}
            </text>
        );
    };

    const columns = [
        {
            title: "Month",
            dataIndex: "month",
            key: "month",
            render: (value: string) => dayjs(value).format("YYYY-MM"),
        },
        {
            title: "Customers",
            dataIndex: "customer_count",
            key: "customer_count",
        },
        {
            title: "THEO (M KRW)",
            dataIndex: "theo",
            key: "theo",
        },
        {
            title: "Action",
            render: (_: any, record: any) => (
                <Button type="link" onClick={() => openModal(record)}>Edit</Button>
            ),
        },
    ];

    const PIE_COLORS = [
        "#1890ff", "#52c41a", "#faad14", "#eb2f96", "#722ed1",
        "#13c2c2", "#a0d911", "#fa541c", "#2f54eb", "#b37feb",
        "#36cfc9", "#ff7a45", "#73d13d"
    ];

    const PieTooltip: React.FC<any> = ({active, payload, total = 0}) => {
        if (!active || !payload || !payload.length) return null;
        const p = payload[0];
        const value = Number(p?.value ?? p?.payload?.value ?? 0);
        const pct =
            p?.percent != null
                ? p.percent * 100
                : total > 0
                    ? (value / total) * 100
                    : 0;

        return (
            <div style={{background: "#fff", border: "1px solid #ccc", padding: 8, borderRadius: 4}}>
                <p><strong>{p?.name ?? p?.payload?.name}</strong></p>
                <p>Count: {value}</p>
                <p>Percentage: {pct.toFixed(1)}%</p>
            </div>
        );
    };

    const nationalityTotal = useMemo(
        () => nationalityData.reduce((sum, d) => sum + (Number(d.value) || 0), 0),
        [nationalityData]
    );

    const ageBucketTotal = useMemo(
        () => ageBucketData.reduce((sum, d) => sum + (Number(d.value) || 0), 0),
        [ageBucketData]
    );

    return (
        <div style={{padding: 24, background: "#f5f5f5", minHeight: "100vh"}}>
            <Title level={2}>CC Analytics</Title>

            {currentUser?.id === 1 && (
                <Card style={{marginBottom: 16}} title="Monthly Report Export (Private)">
                    <Space wrap>
                        <Select
                            value={exportYear}
                            style={{width: 120}}
                            onChange={(val) => setExportYear(val)}
                        >
                            {Array.from({length: 6}).map((_, idx) => {
                                const y = now.year() - idx;
                                return (
                                    <Option key={y} value={y}>
                                        {y}
                                    </Option>
                                );
                            })}
                        </Select>
                        <Select
                            value={exportMonth}
                            style={{width: 120}}
                            onChange={(val) => setExportMonth(val)}
                        >
                            {Array.from({length: 12}).map((_, idx) => (
                                <Option key={idx + 1} value={idx + 1}>
                                    {idx + 1}月
                                </Option>
                            ))}
                        </Select>
                        <Button type="primary" onClick={handleExportPdf}>
                            Export PDF
                        </Button>
                    </Space>
                </Card>
            )}

            <Button type="primary" style={{marginBottom: 16}} onClick={() => openModal()}>
                Add Monthly Data
            </Button>
            <Row gutter={24} style={{marginBottom: 32}}>
                <Col span={24} lg={12}>
                    <Card
                        title={
                            <>
                                Customers per Month
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => setShowCustomerBarChart(prev => !prev)}
                                    style={{float: "right", marginTop: -6}}
                                >
                                    Switch to {showCustomerBarChart ? "Line" : "Bar"}
                                </Button>
                            </>
                        }
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            {showCustomerBarChart ? (
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis dataKey="month" tickFormatter={(val) => dayjs(val).format("YYYY-MM")}/>
                                    <YAxis domain={[0, 'auto']}/>
                                    <Tooltip/>
                                    <Legend/>
                                    <Bar dataKey="customer_count" fill="#1890ff"/>
                                </BarChart>
                            ) : (
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis dataKey="month" tickFormatter={(val) => dayjs(val).format("YYYY-MM")}/>
                                    <YAxis domain={[0, 'auto']}/>
                                    <Tooltip/>
                                    <Legend/>
                                    <Line type="monotone" dataKey="customer_count" stroke="#1890ff"/>
                                </LineChart>
                            )}
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col span={24} lg={12}>
                    <Card
                        title={
                            <>
                                THEO per Month (M KRW)
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => setShowBarChart(prev => !prev)}
                                    style={{float: "right", marginTop: -6}}
                                >
                                    Switch to {showBarChart ? "Line" : "Bar"}
                                </Button>
                            </>
                        }
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            {showBarChart ? (
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis dataKey="month" tickFormatter={(val) => dayjs(val).format("YYYY-MM")}/>
                                    <YAxis domain={[0, 'auto']}/>
                                    <Tooltip/>
                                    <Legend/>
                                    <Bar dataKey="theo" fill="#52c41a"/>
                                </BarChart>
                            ) : (
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <XAxis dataKey="month" tickFormatter={(val) => dayjs(val).format("YYYY-MM")}/>
                                    <YAxis domain={[0, 'auto']}/>
                                    <Tooltip/>
                                    <Legend/>
                                    <Line type="monotone" dataKey="theo" stroke="#52c41a"/>
                                </LineChart>
                            )}
                        </ResponsiveContainer>
                    </Card>
                </Col>

                <Col span={24} lg={12}>
                    <Card title="Monthly Reservations (First Timers vs Repeaters)">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={reservationData}>
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis
                                    dataKey="month"
                                    tickFormatter={(val) => dayjs(val).format("YYYY-MM")}
                                />
                                <YAxis allowDecimals={false}/>
                                <Tooltip/>
                                <Legend/>
                                <Bar dataKey="first_timers" name="First Timers" fill="#faad14"/>
                                <Bar dataKey="repeaters" name="Repeaters" fill="#1890ff"/>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                <Col span={24} lg={12}>
                    <Card title="Unique Visitors Per Month (First Timers vs Repeaters)">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={uniqueReservationData}>
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis
                                    dataKey="month"
                                    tickFormatter={(val) => dayjs(val).format("YYYY-MM")}
                                />
                                <YAxis allowDecimals={false}/>
                                <Tooltip/>
                                <Legend/>
                                <Bar dataKey="first_timers" name="First Timers" fill="#faad14"/>
                                <Bar dataKey="repeaters" name="Repeaters" fill="#1890ff"/>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                <Col span={24} lg={12}>
                    <Card title="Customer Age (5-year Buckets)">
                        <ResponsiveContainer width="100%" height={320}>
                            <PieChart>
                                <Tooltip content={<PieTooltip total={ageBucketTotal}/>}/>
                                <Legend/>
                                <Pie data={ageBucketData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                                     outerRadius={110} label>
                                    {ageBucketData.map((entry, idx) => (
                                        <Cell key={`age-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]}/>
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                <Col span={24} lg={12}>
                    <Card title="Customer Nationality">
                        <ResponsiveContainer width="100%" height={320}>
                            <PieChart>
                                <Tooltip content={<PieTooltip total={nationalityTotal}/>}/>
                                <Legend/>
                                <Pie data={nationalityData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                                     outerRadius={110} label>
                                    {nationalityData.map((entry, idx) => (
                                        <Cell key={`nat-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]}/>
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                <Col span={24} lg={12}>
                    <Card title="Customers by Prefecture (Japan)">
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart
                                data={prefectureData}
                                margin={{top: 10, right: 20, left: 0, bottom: 40}}
                            >
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis
                                    dataKey="name"
                                    angle={-35}
                                    textAnchor="end"
                                    interval={0}
                                    height={70}
                                />
                                <YAxis/>
                                <Tooltip/>
                                <Bar dataKey="value" name="Customers">
                                    {prefectureData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={index % 2 === 0 ? "#1677ff" : "#fa8c16"}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                <Col span={24} lg={12}>
                    <Card title="Top Cities (Japan)">
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart
                                data={cityData}
                                layout="vertical"
                                margin={{top: 10, right: 20, left: 60, bottom: 10}}
                            >
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis type="number"/>
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={160}
                                    interval={0}
                                />
                                <Tooltip cursor={{fill: "rgba(0,0,0,0.05)"}}/>

                                <Bar dataKey="value" name="Customers">
                                    {cityData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={index % 2 === 0 ? "#1677ff" : "#fa8c16"}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                <Col span={24}>
                    <Card title="Customer Distribution in Japan">
                        {mapReady && (
                            <ReactECharts
                                option={japanMapOption}
                                style={{height: 320, width: "100%"}}
                            />
                        )}
                    </Card>
                </Col>

                <Col span={24}>
                    <Card title="New Registrations">
                        <Radio.Group
                            value={registrationGranularity}
                            onChange={(e) => setRegistrationGranularity(e.target.value)}
                            style={{marginBottom: 16}}
                        >
                            <Radio.Button value="daily">Daily</Radio.Button>
                            <Radio.Button value="monthly">Monthly</Radio.Button>
                            <Radio.Button value="quarterly">Quarterly</Radio.Button>
                        </Radio.Group>

                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={
                                    registrationGranularity === "daily"
                                        ? registrationCounts.daily
                                        : registrationGranularity === "monthly"
                                            ? registrationCounts.monthly
                                            : registrationCounts.quarterly
                                }
                            >
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis
                                    dataKey={
                                        registrationGranularity === "daily"
                                            ? "date"
                                            : registrationGranularity === "monthly"
                                                ? "month"
                                                : "quarter"
                                    }
                                    tickFormatter={(val) =>
                                        registrationGranularity === "daily"
                                            ? dayjs(val).format("YYYY-MM-DD")
                                            : registrationGranularity === "monthly"
                                                ? dayjs(val).format("YYYY-MM")
                                                : val
                                    }
                                />
                                <YAxis allowDecimals={false}/>
                                <Tooltip/>
                                <Legend/>
                                <Bar dataKey="count" name="Registrations" fill="#722ed1"/>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>

            <Card title="Monthly Data Table">
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total,
                        showSizeChanger: true,

                        onChange: (page, pageSize) => {
                            setPagination({current: page, pageSize});
                        },

                        onShowSizeChange: (_current, size) => {
                            setPagination({current: 1, pageSize: size}); // reset to page 1
                        },
                    }}
                />
            </Card>

            <Modal
                title={editing ? "Edit Month" : "Add Monthly Data"}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText="Save"
            >
                <Form layout="vertical" form={form} onFinish={handleSave}>
                    <Form.Item name="month" label="Month" rules={[{required: true}]}>
                        <DatePicker picker="month" style={{width: "100%"}}/>
                    </Form.Item>
                    <Form.Item
                        name="customer_count"
                        label="Customer Count"
                        rules={[{required: true}]}
                    >
                        <InputNumber min={0} style={{width: "100%"}}/>
                    </Form.Item>
                    <Form.Item
                        name="theo"
                        label="THEO (in million KRW)"
                        rules={[{required: true}]}
                    >
                        <InputNumber min={0} step={0.1} style={{width: "100%"}}/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CCAnalyticsPage;
